import { Modal } from "bootstrap";
import {
  ApiError,
  createLocation,
  createSubscription,
  deleteSubscription,
  getLocation,
  getSubscriptions,
  searchPlaces,
  updateSubscription,
} from "./api";
import { Autocomplete } from "./autocomplete";
import { isAuthenticated, login, logout } from "./auth";
import { escapeHtml } from "./html";
import { VOIVODESHIPS, voivodeshipName } from "./constants";
import { countyNameByTeryt } from "./state";
import type { AlertField, Comparator, GeocodedPlace, Location, LocationAlertRule, Subscription, SubscriptionPayload } from "./types";

interface CountyOption {
  value: string;
  label: string;
}

interface LocationOption {
  value: string;
  label: string;
}

interface LocationGroup {
  locationId: number;
  label: string;
  latitude: number | null;
  longitude: number | null;
  rules: LocationAlertRule[];
}

const FIELD_LABELS: Record<AlertField, string> = {
  temperature_max: "Temp. maks.",
  temperature_min: "Temp. min.",
  apparent_temperature_max: "Odczuwalna temp. maks.",
  apparent_temperature_min: "Odczuwalna temp. min.",
  wind_speed: "Wiatr",
  wind_gust: "Porywy wiatru",
  precipitation: "Opady",
  snowfall: "Śnieg",
  precipitation_probability: "Prawdopodobieństwo opadów",
  uv_index: "Indeks UV",
  fog: "Mgła",
  thunderstorm: "Burza z piorunami",
  freezing_rain: "Marznący deszcz / gołoledź",
};

const COMPARATOR_LABELS: Record<Comparator, string> = {
  gt: "powyżej",
  lt: "poniżej",
};

interface FieldDefaults {
  comparator: Comparator;
  threshold: number;
  phenomenon: string;
}

const FIELD_DEFAULTS: Record<AlertField, FieldDefaults> = {
  temperature_max: { comparator: "gt", threshold: 30, phenomenon: "upał" },
  temperature_min: { comparator: "lt", threshold: 0, phenomenon: "mróz" },
  apparent_temperature_max: { comparator: "gt", threshold: 32, phenomenon: "upał (odczuwalny)" },
  apparent_temperature_min: { comparator: "lt", threshold: -10, phenomenon: "przenikliwe zimno" },
  wind_speed: { comparator: "gt", threshold: 12, phenomenon: "silny wiatr" },
  wind_gust: { comparator: "gt", threshold: 20, phenomenon: "porywisty wiatr" },
  precipitation: { comparator: "gt", threshold: 20, phenomenon: "ulewa" },
  snowfall: { comparator: "gt", threshold: 5, phenomenon: "opady śniegu" },
  precipitation_probability: { comparator: "gt", threshold: 70, phenomenon: "duże ryzyko opadów" },
  uv_index: { comparator: "gt", threshold: 8, phenomenon: "silne promieniowanie UV" },
  fog: { comparator: "gt", threshold: 0.5, phenomenon: "mgła" },
  thunderstorm: { comparator: "gt", threshold: 0.5, phenomenon: "burza z piorunami" },
  freezing_rain: { comparator: "gt", threshold: 0.5, phenomenon: "marznący deszcz / gołoledź" },
};

// Default threshold + phenomenon per comparator direction, for fields where "powyżej"
// and "poniżej" mean genuinely different phenomena (e.g. temperature: upał vs. mróz).
// Falls back to FIELD_DEFAULTS when a field has no direction-specific override.
const COMPARATOR_DEFAULT_OVERRIDES: Partial<Record<AlertField, Partial<Record<Comparator, Pick<FieldDefaults, "threshold" | "phenomenon">>>>> = {
  temperature_max: { gt: { threshold: 30, phenomenon: "upał" }, lt: { threshold: 0, phenomenon: "mróz" } },
  temperature_min: { gt: { threshold: 30, phenomenon: "upał" }, lt: { threshold: 0, phenomenon: "mróz" } },
  apparent_temperature_max: {
    gt: { threshold: 32, phenomenon: "upał (odczuwalny)" },
    lt: { threshold: -10, phenomenon: "przenikliwe zimno" },
  },
  apparent_temperature_min: {
    gt: { threshold: 32, phenomenon: "upał (odczuwalny)" },
    lt: { threshold: -10, phenomenon: "przenikliwe zimno" },
  },
};

const BOOLEAN_FIELDS = new Set<AlertField>(["fog", "thunderstorm", "freezing_rain"]);

const FIELD_UNITS: Partial<Record<AlertField, string>> = {
  temperature_max: "°C",
  temperature_min: "°C",
  apparent_temperature_max: "°C",
  apparent_temperature_min: "°C",
  wind_speed: "m/s",
  wind_gust: "m/s",
  precipitation: "mm",
  snowfall: "cm",
  precipitation_probability: "%",
};

// Covers every place_type value the geocoding DB actually contains (checked live:
// SELECT place_type, count(*) FROM points/places GROUP BY place_type) — a raw OSM
// tag slipping through untranslated (e.g. "peak" for Turbacz) reads as a bug.
const PLACE_TYPE_PL: Record<string, string> = {
  city: "miasto",
  town: "miasteczko",
  village: "wieś",
  hamlet: "przysiółek",
  suburb: "dzielnica",
  neighbourhood: "osiedle",
  locality: "miejscowość",
  peak: "szczyt",
  saddle: "przełęcz",
  ridge: "grzbiet górski",
  valley: "dolina",
  gorge: "wąwóz",
  cave: "jaskinia",
  rock: "skała",
  viewpoint: "punkt widokowy",
  cemetery: "cmentarz",
  lake: "jezioro",
  river: "rzeka",
  waterfall: "wodospad",
  island: "wyspa",
  islet: "wysepka",
  beach: "plaża",
  wetland: "mokradła",
  heath: "wrzosowisko",
  forest: "las",
  park: "park",
  nature_reserve: "rezerwat przyrody",
  national_park: "park narodowy",
  wilderness_hut: "schronisko turystyczne",
  alpine_hut: "schronisko górskie",
  castle: "zamek",
  castle_ruins: "ruiny zamku",
  ruins: "ruiny",
  manor: "dwór",
  palace: "pałac",
  fort: "fort",
  monastery: "klasztor",
  lighthouse: "latarnia morska",
};

interface PlaceHintSource {
  place_type: string | null;
  voivodeship: string | null;
  county: string | null;
  municipality: string | null;
}

function placeHint(place: PlaceHintSource): string | null {
  if (!place.place_type) return null;
  const type = PLACE_TYPE_PL[place.place_type] ?? place.place_type;
  const gmina = place.municipality ?? "?";
  const powiat = place.county ?? "?";
  const woj = place.voivodeship ?? "?";
  return `${type} — gmina: ${gmina}, powiat: ${powiat}, woj.: ${woj}`;
}

function locationDisplayLabel(location: Pick<Location, "name"> & PlaceHintSource): string {
  const hint = placeHint(location);
  return hint ? `${location.name} (${hint})` : location.name;
}

let voivodeshipSelect: HTMLSelectElement;
let countySearchInput: HTMLInputElement;
let selectedCountiesContainer: HTMLElement;
let subscriptionListEl: HTMLElement;
let settingsPanelEl: HTMLElement;
let emptyStateEl: HTMLElement;
let formEl: HTMLFormElement;
let formErrorEl: HTMLElement;
let formSuccessEl: HTMLElement;
let nameInput: HTMLInputElement;
let urlInput: HTMLInputElement;
let notifyMeteoInput: HTMLInputElement;
let notifyHydroInput: HTMLInputElement;
let submitButton: HTMLButtonElement;
let cancelEditButton: HTMLButtonElement;
let countyAutocomplete: Autocomplete<CountyOption>;
let locationSearchInput: HTMLInputElement;
let locationGroupsContainer: HTMLElement;
let forecastDaysInput: HTMLInputElement;
let imgwSectionEl: HTMLElement;
let forecastSectionEl: HTMLElement;

type SubscriptionFormType = "imgw" | "forecast";

let selectedCounties: string[] = [];
let locationGroups: LocationGroup[] = [];
let editingSubscriptionId: number | null = null;
const pendingLocationKeys = new Set<string>();
let currentFormType: SubscriptionFormType | null = null;
let onSaved: () => void = () => {};
let settingsModal: Modal | null = null;
let settingsModalOpen = false;
let floatingPanelElRefs: HTMLElement[] = [];

// The backdrop's semi-transparent overlay barely dims other floating panels
// (map legend, map search) — they have their own near-opaque backgrounds, so
// their text/borders stay fully legible showing through regardless of
// z-index. Hide them outright instead while settings is open.
function setFloatingPanelsHidden(hidden: boolean): void {
  for (const el of floatingPanelElRefs) el.classList.toggle("hidden", hidden);
}

function renderSelectedCounties(): void {
  selectedCountiesContainer.innerHTML = selectedCounties
    .map((terytCode) => {
      const name = countyNameByTeryt.get(terytCode) ?? terytCode;
      return `<span class="county-chip" data-teryt="${escapeHtml(terytCode)}">${escapeHtml(name)}<button type="button" aria-label="usuń powiat ${escapeHtml(name)}">✕</button></span>`;
    })
    .join("");
}

function scopeDescription(subscription: Subscription): string | null {
  if (subscription.county_teryt_codes.length > 0) {
    const names = subscription.county_teryt_codes.map((code) => countyNameByTeryt.get(code) ?? code);
    return `powiaty: ${names.join(", ")}`;
  }
  if (subscription.voivodeship_teryt_code) {
    return `woj. ${voivodeshipName(subscription.voivodeship_teryt_code)}`;
  }
  return null;
}

function subscriptionSummaryLabel(subscription: Subscription): string {
  return (
    subscription.name ||
    scopeDescription(subscription) ||
    locationRulesDescription(subscription) ||
    "subskrypcja"
  );
}

function ruleSummary(rule: LocationAlertRule): string {
  if (BOOLEAN_FIELDS.has(rule.field)) return rule.phenomenon;
  const unit = FIELD_UNITS[rule.field] ?? "";
  return `${rule.phenomenon} ${COMPARATOR_LABELS[rule.comparator]} ${rule.threshold}${unit}`;
}

function locationRulesDescription(subscription: Subscription): string | null {
  if (subscription.location_alert_rules.length === 0) return null;
  const placeCount = new Set(subscription.location_alert_rules.map((rule) => rule.location_id)).size;
  const parts = subscription.location_alert_rules.map(ruleSummary);
  return `pogoda (${placeCount} ${placeCount === 1 ? "miejsce" : "miejsc"}, ${subscription.forecast_days_ahead} dni): ${parts.join(", ")}`;
}

function renderLocationGroups(): void {
  locationGroupsContainer.innerHTML = locationGroups
    .map((group, groupIndex) => {
      const usedFieldsInGroup = new Set(group.rules.map((rule) => rule.field));
      const rulesHtml = group.rules
        .map((rule, ruleIndex) => {
          const isBoolean = BOOLEAN_FIELDS.has(rule.field);
          const fieldOptions = (Object.keys(FIELD_LABELS) as AlertField[])
            .filter((field) => field === rule.field || !usedFieldsInGroup.has(field))
            .map((field) => `<option value="${field}" ${field === rule.field ? "selected" : ""}>${FIELD_LABELS[field]}</option>`)
            .join("");
          const comparatorOptions = (Object.keys(COMPARATOR_LABELS) as Comparator[])
            .map((comparator) => `<option value="${comparator}" ${comparator === rule.comparator ? "selected" : ""}>${COMPARATOR_LABELS[comparator]}</option>`)
            .join("");
          const unit = FIELD_UNITS[rule.field] ?? "";
          return `
            <div class="location-rule-row${isBoolean ? " boolean-rule" : ""}" data-group-index="${groupIndex}" data-rule-index="${ruleIndex}">
              <select class="rule-field" aria-label="Pole">${fieldOptions}</select>
              <select class="rule-comparator${isBoolean ? " hidden-input" : ""}" aria-label="Porównanie">${comparatorOptions}</select>
              <input type="number" class="rule-threshold${isBoolean ? " hidden-input" : ""}" step="0.1" value="${rule.threshold}" aria-label="Próg" />
              <span class="rule-unit${isBoolean || !unit ? " hidden-input" : ""}">${unit}</span>
              <input type="text" class="rule-phenomenon" value="${escapeHtml(rule.phenomenon)}" placeholder="np. upał" aria-label="Nazwa" />
              <button type="button" class="rule-remove" aria-label="Usuń regułę">✕</button>
            </div>
          `;
        })
        .join("");
      const allFieldsUsed = usedFieldsInGroup.size >= (Object.keys(FIELD_LABELS) as AlertField[]).length;
      return `
        <div class="location-group" data-group-index="${groupIndex}">
          <div class="location-group-header">
            <span class="location-group-label">${escapeHtml(group.label)}</span>
            <button type="button" class="location-group-remove" aria-label="Usuń miejsce">✕ usuń miejsce</button>
          </div>
          <div class="location-group-rules" role="group" aria-label="Reguły alarmowe: ${escapeHtml(group.label)}">${rulesHtml}</div>
          <button type="button" class="add-location-rule secondary-button" data-group-index="${groupIndex}" ${allFieldsUsed ? "disabled" : ""}>+ dodaj regułę</button>
        </div>
      `;
    })
    .join("");
}

function categoriesDescription(subscription: Subscription): string {
  if (subscription.location_alert_rules.length > 0) return "alert pogodowy";
  if (subscription.notify_meteo && subscription.notify_hydro) return "meteo + hydro";
  if (subscription.notify_meteo) return "tylko meteo";
  if (subscription.notify_hydro) return "tylko hydro";
  return "brak kategorii";
}

function parsePositiveInt(value: string, fallback: number): number {
  const parsed = Number(value);
  return value.trim() !== "" && Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.status === 401) {
    return "Sesja wygasła — zaloguj się ponownie.";
  }
  return error instanceof ApiError ? error.message : fallback;
}

function setFormError(message: string | null): void {
  formErrorEl.textContent = message ?? "";
  formErrorEl.classList.toggle("hidden", !message);
}

let successTimeoutId: ReturnType<typeof setTimeout> | undefined;

function showFormSuccess(message: string): void {
  formSuccessEl.textContent = message;
  formSuccessEl.classList.remove("hidden");
  clearTimeout(successTimeoutId);
  successTimeoutId = setTimeout(() => formSuccessEl.classList.add("hidden"), 4000);
}

async function loadSubscriptions(): Promise<void> {
  let subscriptions: Subscription[];
  try {
    subscriptions = await getSubscriptions();
  } catch (error) {
    setFormError(errorMessage(error, "Nie udało się wczytać subskrypcji."));
    return;
  }

  subscriptionListEl.innerHTML = "";
  for (const subscription of subscriptions) {
    const li = document.createElement("li");

    const label = document.createElement("span");
    label.className = "subscription-label";

    if (subscription.name) {
      const nameEl = document.createElement("span");
      nameEl.className = "subscription-name";
      nameEl.textContent = subscription.name;
      label.appendChild(nameEl);
    }

    const scope = scopeDescription(subscription);
    if (scope) {
      const scopeEl = document.createElement("span");
      scopeEl.className = "subscription-scope";
      scopeEl.textContent = scope;
      label.appendChild(scopeEl);
    }

    const categoriesEl = document.createElement("span");
    categoriesEl.className = "subscription-categories";
    categoriesEl.textContent = categoriesDescription(subscription);
    label.appendChild(categoriesEl);

    const locationRules = locationRulesDescription(subscription);
    if (locationRules) {
      const locationRulesEl = document.createElement("span");
      locationRulesEl.className = "subscription-categories";
      locationRulesEl.textContent = locationRules;
      label.appendChild(locationRulesEl);
    }

    const actions = document.createElement("div");
    actions.className = "subscription-actions";

    const deleteButton = document.createElement("button");
    deleteButton.type = "button";
    deleteButton.textContent = "usuń";
    // Delete stays its own button (with its own confirm dialog) — stop the
    // click from also bubbling up into the row's edit handler below.
    deleteButton.addEventListener("click", (event) => {
      event.stopPropagation();
      void handleDelete(subscription);
    });

    actions.appendChild(deleteButton);

    li.classList.add("subscription-row");
    li.setAttribute("role", "button");
    li.tabIndex = 0;
    li.setAttribute("aria-label", `Edytuj subskrypcję ${subscriptionSummaryLabel(subscription)}`);
    li.addEventListener("click", () => void startEditingSubscription(subscription));
    li.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      void startEditingSubscription(subscription);
    });

    li.appendChild(label);
    li.appendChild(actions);
    subscriptionListEl.appendChild(li);
  }
}

async function handleDelete(subscription: Subscription): Promise<void> {
  const label = subscription.name
    ? `${subscription.name}${scopeDescription(subscription) ? ` (${scopeDescription(subscription)})` : ""}`
    : subscriptionSummaryLabel(subscription);
  if (!confirm(`Usunąć subskrypcję „${label}”? Tej operacji nie można cofnąć.`)) {
    return;
  }

  try {
    await deleteSubscription(subscription.id);
  } catch (error) {
    setFormError(errorMessage(error, "Nie udało się usunąć subskrypcji."));
    return;
  }
  if (editingSubscriptionId === subscription.id) showEmptyState();
  await loadSubscriptions();
  onSaved();
}

function resetFormFields(): void {
  formEl.reset();
  selectedCounties = [];
  renderSelectedCounties();
  locationGroups = [];
  renderLocationGroups();
  forecastDaysInput.value = "3";
  setFormError(null);
}

function showSectionsForType(type: SubscriptionFormType): void {
  imgwSectionEl.classList.toggle("hidden", type !== "imgw");
  forecastSectionEl.classList.toggle("hidden", type !== "forecast");
}

// Single source of truth for submission: reads which section is actually visible
// instead of trusting `currentFormType`, so the two can never drift apart.
function visibleFormType(): SubscriptionFormType {
  return forecastSectionEl.classList.contains("hidden") ? "imgw" : "forecast";
}

function showEmptyState(): void {
  editingSubscriptionId = null;
  currentFormType = null;
  resetFormFields();
  emptyStateEl.classList.remove("hidden");
  formEl.classList.add("hidden");
  // Mobile: show the subscription list full-screen instead of the form pane.
  settingsPanelEl.classList.remove("form-active");
}

function showNewSubscriptionForm(type: SubscriptionFormType): void {
  editingSubscriptionId = null;
  currentFormType = type;
  resetFormFields();
  showSectionsForType(type);
  submitButton.textContent = type === "imgw" ? "Dodaj alert IMGW" : "Dodaj alert pogodowy";
  emptyStateEl.classList.add("hidden");
  formEl.classList.remove("hidden");
  nameInput.focus();
  // Mobile: switch to the form pane full-screen instead of the list.
  settingsPanelEl.classList.add("form-active");
}

async function startEditingSubscription(subscription: Subscription): Promise<void> {
  editingSubscriptionId = subscription.id;
  currentFormType = subscription.location_alert_rules.length > 0 ? "forecast" : "imgw";
  emptyStateEl.classList.add("hidden");
  formEl.classList.remove("hidden");
  settingsPanelEl.classList.add("form-active");
  showSectionsForType(currentFormType);
  submitButton.textContent = "Zapisz zmiany";
  nameInput.value = subscription.name;
  urlInput.value = subscription.discord_webhook_url;
  notifyMeteoInput.checked = subscription.notify_meteo;
  notifyHydroInput.checked = subscription.notify_hydro;

  if (subscription.county_teryt_codes.length > 0) {
    selectedCounties = [...subscription.county_teryt_codes];
    voivodeshipSelect.value = selectedCounties[0].slice(0, 2);
  } else {
    selectedCounties = [];
    voivodeshipSelect.value = subscription.voivodeship_teryt_code ?? VOIVODESHIPS[0][0];
  }
  renderSelectedCounties();
  countySearchInput.value = "";

  forecastDaysInput.value = String(subscription.forecast_days_ahead);
  locationGroups = [];
  renderLocationGroups();
  locationSearchInput.value = "";

  setFormError(null);
  nameInput.focus();

  const requestId = subscription.id;
  const locationIds = [...new Set(subscription.location_alert_rules.map((rule) => rule.location_id))];
  const resolvedGroups = await Promise.all(
    locationIds.map(async (locationId) => {
      let label = `lokalizacja #${locationId}`;
      let latitude: number | null = null;
      let longitude: number | null = null;
      try {
        const location = await getLocation(locationId);
        label = locationDisplayLabel(location);
        latitude = location.latitude;
        longitude = location.longitude;
      } catch {
        // location may have been removed independently — keep the fallback label
      }
      return {
        locationId,
        label,
        latitude,
        longitude,
        rules: subscription.location_alert_rules.filter((rule) => rule.location_id === locationId).map((rule) => ({ ...rule })),
      };
    }),
  );

  if (editingSubscriptionId !== requestId) return;
  locationGroups = resolvedGroups;
  renderLocationGroups();
}

function validateFormForType(type: SubscriptionFormType | null): string | null {
  if (type === "imgw" && !notifyMeteoInput.checked && !notifyHydroInput.checked) {
    return "Wybierz przynajmniej jedną kategorię ostrzeżeń (METEO lub HYDRO).";
  }
  if (type === "forecast" && locationGroups.every((group) => group.rules.length === 0)) {
    return "Dodaj przynajmniej jedno miejsce z regułą progową.";
  }
  const forecastDays = Number(forecastDaysInput.value);
  if (type === "forecast" && (!Number.isFinite(forecastDays) || forecastDays < 1 || forecastDays > 10)) {
    return "Liczba dni prognozy musi być z zakresu 1–10.";
  }
  if (type === "forecast") {
    const seen = new Set<string>();
    for (const group of locationGroups) {
      for (const rule of group.rules) {
        if (!rule.phenomenon.trim()) {
          return "Podaj nazwę zjawiska dla każdej reguły.";
        }
        const key = `${rule.field}|${rule.comparator}|${rule.threshold}`;
        if (seen.has(key)) {
          return "Dwie reguły dla tego samego miejsca mają identyczne pole, porównanie i próg — usuń duplikat.";
        }
        seen.add(key);
      }
      seen.clear();
    }
  }
  return null;
}

async function handleSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  setFormError(null);

  // Creating a subscription can take several seconds (the server posts to
  // Discord synchronously on create) with no loading indicator otherwise —
  // a second click before the first request resolves used to create a
  // duplicate subscription (and duplicate Discord notifications forever after).
  if (submitButton.disabled) return;

  const type = visibleFormType();
  const validationError = validateFormForType(type);
  if (validationError) {
    setFormError(validationError);
    return;
  }

  const wasEditing = editingSubscriptionId !== null;
  const payload: SubscriptionPayload = {
    discord_webhook_url: urlInput.value.trim(),
    name: nameInput.value.trim(),
    is_active: true,
    notify_meteo: notifyMeteoInput.checked,
    notify_hydro: notifyHydroInput.checked,
    county_teryt_codes: type === "forecast" ? [] : selectedCounties,
    voivodeship_teryt_code: type === "forecast" ? null : selectedCounties.length > 0 ? null : voivodeshipSelect.value || null,
    location_alert_rules: type === "imgw" ? [] : locationGroups.flatMap((group) => group.rules),
    forecast_days_ahead: parsePositiveInt(forecastDaysInput.value, 3),
  };

  const originalButtonText = submitButton.textContent;
  submitButton.disabled = true;
  submitButton.textContent = "Zapisywanie…";

  try {
    if (editingSubscriptionId !== null) {
      await updateSubscription(editingSubscriptionId, payload);
    } else {
      await createSubscription(payload);
    }
  } catch (error) {
    setFormError(errorMessage(error, "Nie udało się zapisać subskrypcji."));
    return;
  } finally {
    submitButton.disabled = false;
    submitButton.textContent = originalButtonText;
  }

  showFormSuccess(wasEditing ? "Zapisano zmiany." : "Dodano subskrypcję.");

  showEmptyState();
  await loadSubscriptions();
  onSaved();
}

export function initSubscriptions(ids: {
  settingsPanel: string;
  settingsBackdrop: string;
  settingsToggle: string;
  settingsClose: string;
  settingsContent: string;
  loginGate: string;
  loginButton: string;
  logoutButton: string;
  form: string;
  formError: string;
  formSuccess: string;
  nameInput: string;
  urlInput: string;
  voivodeshipSelect: string;
  countySearchInput: string;
  countySearchResults: string;
  selectedCounties: string;
  notifyMeteo: string;
  notifyHydro: string;
  submitButton: string;
  cancelEditButton: string;
  list: string;
  addImgw: string;
  addForecast: string;
  emptyState: string;
  imgwSection: string;
  forecastSection: string;
  locationSearchInput: string;
  locationSearchResults: string;
  locationGroups: string;
  forecastDaysAhead: string;
  onSaved: () => void;
}): void {
  onSaved = ids.onSaved;
  settingsPanelEl = document.getElementById(ids.settingsPanel) as HTMLElement;
  const settingsBackdropEl = document.getElementById(ids.settingsBackdrop) as HTMLElement;
  floatingPanelElRefs = [document.getElementById("panel"), document.getElementById("map-search")].filter(
    (el): el is HTMLElement => el !== null,
  );
  const settingsContent = document.getElementById(ids.settingsContent) as HTMLElement;
  const loginGate = document.getElementById(ids.loginGate) as HTMLElement;
  const loginButton = document.getElementById(ids.loginButton) as HTMLButtonElement;
  const logoutButton = document.getElementById(ids.logoutButton) as HTMLButtonElement;
  emptyStateEl = document.getElementById(ids.emptyState) as HTMLElement;
  formEl = document.getElementById(ids.form) as HTMLFormElement;
  formErrorEl = document.getElementById(ids.formError) as HTMLElement;
  formSuccessEl = document.getElementById(ids.formSuccess) as HTMLElement;
  nameInput = document.getElementById(ids.nameInput) as HTMLInputElement;
  urlInput = document.getElementById(ids.urlInput) as HTMLInputElement;

  nameInput.addEventListener("invalid", () => {
    nameInput.setCustomValidity(nameInput.value.trim() ? "" : "Podaj nazwę subskrypcji.");
  });
  nameInput.addEventListener("input", () => nameInput.setCustomValidity(""));

  urlInput.addEventListener("invalid", () => {
    urlInput.setCustomValidity(
      urlInput.value.trim()
        ? "Podaj poprawny adres URL, zaczynający się od http:// lub https://."
        : "Podaj adres webhooka Discord.",
    );
  });
  urlInput.addEventListener("input", () => urlInput.setCustomValidity(""));
  voivodeshipSelect = document.getElementById(ids.voivodeshipSelect) as HTMLSelectElement;
  countySearchInput = document.getElementById(ids.countySearchInput) as HTMLInputElement;
  const countySearchResults = document.getElementById(ids.countySearchResults) as HTMLElement;
  selectedCountiesContainer = document.getElementById(ids.selectedCounties) as HTMLElement;
  notifyMeteoInput = document.getElementById(ids.notifyMeteo) as HTMLInputElement;
  notifyHydroInput = document.getElementById(ids.notifyHydro) as HTMLInputElement;
  submitButton = document.getElementById(ids.submitButton) as HTMLButtonElement;
  cancelEditButton = document.getElementById(ids.cancelEditButton) as HTMLButtonElement;
  subscriptionListEl = document.getElementById(ids.list) as HTMLElement;
  const addImgwButton = document.getElementById(ids.addImgw) as HTMLButtonElement;
  const addForecastButton = document.getElementById(ids.addForecast) as HTMLButtonElement;
  imgwSectionEl = document.getElementById(ids.imgwSection) as HTMLElement;
  forecastSectionEl = document.getElementById(ids.forecastSection) as HTMLElement;
  locationSearchInput = document.getElementById(ids.locationSearchInput) as HTMLInputElement;
  const locationSearchResults = document.getElementById(ids.locationSearchResults) as HTMLElement;
  locationGroupsContainer = document.getElementById(ids.locationGroups) as HTMLElement;
  forecastDaysInput = document.getElementById(ids.forecastDaysAhead) as HTMLInputElement;

  for (const [code, name] of VOIVODESHIPS) {
    const option = document.createElement("option");
    option.value = code;
    option.textContent = name;
    voivodeshipSelect.appendChild(option);
  }

  countyAutocomplete = new Autocomplete<CountyOption>({
    input: countySearchInput,
    resultsContainer: countySearchResults,
    showOnEmptyFocus: true,
    emptyMessage: "Brak powiatów",
    getItems: (query) => {
      const q = query.trim().toLocaleLowerCase("pl-PL");
      const voivodeshipPrefix = voivodeshipSelect.value;
      return [...countyNameByTeryt.entries()]
        .filter(([terytCode]) => terytCode.startsWith(voivodeshipPrefix))
        .filter(([terytCode]) => !selectedCounties.includes(terytCode))
        .filter(([, name]) => !q || name.toLocaleLowerCase("pl-PL").includes(q))
        .sort((a, b) => a[1].localeCompare(b[1], "pl"))
        .map(([terytCode, name]) => ({ value: terytCode, label: name }));
    },
    onSelect: (item) => {
      if (!selectedCounties.includes(item.value)) {
        selectedCounties.push(item.value);
        renderSelectedCounties();
      }
      countySearchInput.value = "";
    },
  });

  selectedCountiesContainer.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const chip = target.closest<HTMLElement>(".county-chip");
    if (!chip || target.tagName !== "BUTTON") return;
    selectedCounties = selectedCounties.filter((terytCode) => terytCode !== chip.dataset.teryt);
    renderSelectedCounties();
    countyAutocomplete.refresh();
  });

  voivodeshipSelect.addEventListener("change", () => {
    selectedCounties = [];
    renderSelectedCounties();
    countySearchInput.value = "";
  });

  new Autocomplete<LocationOption & { place: GeocodedPlace }>({
    input: locationSearchInput,
    resultsContainer: locationSearchResults,
    debounceMs: 400,
    loadingMessage: "Szukam…",
    emptyMessage: "Brak wyników",
    errorMessage: "Nie udało się wyszukać. Spróbuj ponownie.",
    getItems: async (query) => {
      const q = query.trim();
      if (!q) return [];
      const results = await searchPlaces(q);
      return results.map((place) => ({
        value: `${place.latitude},${place.longitude}`,
        label: place.name,
        hint: placeHint(place) ?? undefined,
        place,
      }));
    },
    onSelect: (item) => {
      const key = `${item.place.latitude},${item.place.longitude}`;
      const alreadyAdded = locationGroups.some(
        (group) => group.latitude === item.place.latitude && group.longitude === item.place.longitude,
      );
      locationSearchInput.value = "";
      if (alreadyAdded || pendingLocationKeys.has(key)) return;
      pendingLocationKeys.add(key);

      void (async () => {
        try {
          const location = await createLocation(item.place);
          locationGroups.push({
            locationId: location.id,
            label: locationDisplayLabel(location),
            latitude: item.place.latitude,
            longitude: item.place.longitude,
            rules: [],
          });
          renderLocationGroups();
        } catch (error) {
          setFormError(errorMessage(error, "Nie udało się zapisać lokalizacji."));
        } finally {
          pendingLocationKeys.delete(key);
        }
      })();
    },
  });

  locationGroupsContainer.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    if (target.classList.contains("location-group-remove")) {
      const groupEl = target.closest<HTMLElement>(".location-group");
      if (!groupEl) return;
      const groupIndex = Number(groupEl.dataset.groupIndex);
      locationGroups.splice(groupIndex, 1);
      renderLocationGroups();
      locationSearchInput.focus();
      return;
    }

    if (target.classList.contains("add-location-rule")) {
      const groupIndex = Number(target.dataset.groupIndex);
      const group = locationGroups[groupIndex];
      if (!group) return;
      const usedFields = new Set(group.rules.map((rule) => rule.field));
      const nextField = (Object.keys(FIELD_LABELS) as AlertField[]).find((field) => !usedFields.has(field));
      if (!nextField) return;
      // Same defaults a user gets by explicitly picking this field from the
      // dropdown (see the "rule-field" input handler below) — temperature_max
      // used to be special-cased to a blank phenomenon here since it's first
      // in FIELD_LABELS and so never fires that handler on add, which just
      // made it the one field that didn't auto-fill like all the others.
      group.rules.push({
        ...FIELD_DEFAULTS[nextField],
        location_id: group.locationId,
        field: nextField,
      });
      renderLocationGroups();
      locationGroupsContainer
        .querySelector<HTMLElement>(`.location-rule-row[data-group-index="${groupIndex}"][data-rule-index="${group.rules.length - 1}"] .rule-field`)
        ?.focus();
      return;
    }

    if (target.classList.contains("rule-remove")) {
      const row = target.closest<HTMLElement>(".location-rule-row");
      if (!row) return;
      const groupIndex = Number(row.dataset.groupIndex);
      const group = locationGroups[groupIndex];
      if (!group) return;
      group.rules.splice(Number(row.dataset.ruleIndex), 1);
      renderLocationGroups();
      locationGroupsContainer
        .querySelector<HTMLElement>(`.add-location-rule[data-group-index="${groupIndex}"]`)
        ?.focus();
    }
  });

  locationGroupsContainer.addEventListener("input", (event) => {
    const target = event.target as HTMLElement;
    const row = target.closest<HTMLElement>(".location-rule-row");
    if (!row) return;
    const group = locationGroups[Number(row.dataset.groupIndex)];
    const rule = group?.rules[Number(row.dataset.ruleIndex)];
    if (!rule) return;

    if (target.classList.contains("rule-field")) {
      const field = (target as HTMLSelectElement).value as AlertField;
      rule.field = field;
      Object.assign(rule, FIELD_DEFAULTS[field]);
      renderLocationGroups();
      return;
    }
    if (target.classList.contains("rule-comparator")) {
      const comparator = (target as HTMLSelectElement).value as Comparator;
      rule.comparator = comparator;
      const override = COMPARATOR_DEFAULT_OVERRIDES[rule.field]?.[comparator];
      if (override) Object.assign(rule, override);
      renderLocationGroups();
      return;
    }
    if (target.classList.contains("rule-threshold")) rule.threshold = Number((target as HTMLInputElement).value) || 0;
    if (target.classList.contains("rule-phenomenon")) rule.phenomenon = (target as HTMLInputElement).value;
  });

  cancelEditButton.addEventListener("click", () => {
    showEmptyState();
    settingsModal?.hide();
  });
  formEl.addEventListener("submit", (event) => void handleSubmit(event));
  addImgwButton.addEventListener("click", () => showNewSubscriptionForm("imgw"));
  addForecastButton.addEventListener("click", () => showNewSubscriptionForm("forecast"));

  function renderAuthGate(): void {
    const authenticated = isAuthenticated();
    loginGate.classList.toggle("hidden", authenticated);
    settingsContent.classList.toggle("hidden", !authenticated);
    logoutButton.classList.toggle("hidden", !authenticated);
  }

  const settingsCloseButton = document.getElementById(ids.settingsClose) as HTMLButtonElement;
  const settingsToggleButton = document.getElementById(ids.settingsToggle) as HTMLButtonElement;

  // Bootstrap's Modal owns show/hide, the backdrop element, focus-trapping,
  // Escape-to-close, and click-outside-to-close — we only react to its
  // lifecycle events for the app-specific bits (floating panels, form reset,
  // auth-gated content) it has no way to know about.
  settingsModal = new Modal(settingsBackdropEl);
  settingsBackdropEl.addEventListener("show.bs.modal", () => {
    settingsModalOpen = true;
    setFloatingPanelsHidden(true);
    renderAuthGate();
    showEmptyState();
    if (isAuthenticated()) void loadSubscriptions();
  });
  settingsBackdropEl.addEventListener("hidden.bs.modal", () => {
    settingsModalOpen = false;
    setFloatingPanelsHidden(false);
    showEmptyState();
  });

  settingsToggleButton.addEventListener("click", () => {
    if (settingsModalOpen) {
      settingsModal?.hide();
    } else {
      settingsModal?.show();
    }
  });
  settingsCloseButton.addEventListener("click", () => settingsModal?.hide());
  loginButton.addEventListener("click", () => void login());
  logoutButton.addEventListener("click", () =>
    void logout().then(() => {
      renderAuthGate();
    }),
  );
}
